import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Drawer,
  InputBase,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { ChangeEvent, type FC, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { PMSetupSteps, PMSetupStepsProps } from "../../pm-components";
import { PMButton } from "../../pm-components/pm-button/pm-button";
import type { MenuEntryInterface } from "../../types/menu-type";
import { MenuList } from "../menu-list/menu-list";
import { SidebarHeader } from "../sidebar-header/sidebar-header";

export type SidebarSearchFunction = (
  items: MenuEntryInterface["items"],
  query: string
) => MenuEntryInterface["items"];

export const DEFAULT_SIDEBAR_SEARCH_FUNCTION: SidebarSearchFunction = (
  items,
  query
) =>
  items?.filter(({ title }) =>
    title.toLowerCase().includes(query?.toLowerCase())
  );

export type SidebarProps = {
  /** (optional) set to true if searching the menu entries should be enabled */
  hasSearchFunctionality?: boolean;
  /** (optional) side bar elements */
  entries?: MenuEntryInterface[];
  /** (optional) custom search function */
  searchFunction?: SidebarSearchFunction;
  /** current status of sidebar (open/closed) */
  sidebarOpen: boolean;
  /** width of Sidebar in px */
  width: number;
  /** (optional) set to true if the current page is a settings page */
  hasBanner?: boolean;
  /** (optional) Configuration for the product setup step counter */
  setupSteps?: Omit<PMSetupStepsProps, "title">;
  openSetupScreen?: () => void;
  language?: SupportedLanguage;

  buttonProps?: React.ComponentProps<typeof PMButton> & {
    tourid?: string;
  };
};

export const MenuSection = ({
  entries,
  hasSearchFunctionality,
  searchFunction = DEFAULT_SIDEBAR_SEARCH_FUNCTION,
  buttonProps,
}: Pick<
  SidebarProps,
  "entries" | "hasSearchFunctionality" | "searchFunction" | "buttonProps"
>) => {
  const collator = Intl.Collator(undefined, { numeric: true });
  const { pathname } = useLocation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const filteredEntries = useMemo<MenuEntryInterface[]>(() => {
    return (entries ?? []).map((entry: MenuEntryInterface) => ({
      ...entry,
      active: entry.active || entry.link === pathname,
      items: searchFunction?.(entry.items, searchQuery)?.sort((a, b) =>
        collator.compare(a.title, b.title)
      ),
    }));
  }, [entries, pathname, searchQuery, searchFunction, collator]);

  return (
    <Box
      sx={{
        margin: {
          sm: "0 0 0 21px",
        },
        maxHeight: buttonProps ? "80vh" : "none",
        overflow: "auto",
        marginTop: `env(safe-area-inset-top) !important`,
      }}
    >
      {hasSearchFunctionality && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderRadius: "12px",
            borderColor: theme.palette.secondary.dark,
            backgroundColor: "#FAFAFA",
            "&:hover": {
              backgroundColor: "#FCFCFC",
            },
            mb: 1,
          }}
        >
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ "aria-label": "search" }}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
            />
          </Search>
        </Box>
      )}
      <MenuList entries={filteredEntries} />
    </Box>
  );
};

/** Sidebar component used for navigation */
export const Sidebar: FC<SidebarProps> = ({
  entries = [],
  hasSearchFunctionality,
  searchFunction,
  sidebarOpen,
  width,
  hasBanner,
  buttonProps,
  setupSteps,
  openSetupScreen,
  language = "en",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <aside data-testid="Sidebar">
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={sidebarOpen}
        sx={{
          display: "block",
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",

            width: width,
            border: "none",
          },
        }}
      >
        {hasBanner && <SidebarHeader />}
        <Toolbar sx={{ mb: "20px" }} />
        {setupSteps && (
          <Box
            sx={{
              margin: {
                sm: "4px 0 16px 21px",
              },
              padding: 3,
              border: "1px solid #D9D9D9",
              borderRadius: "12px",
              overflow: "auto",
              cursor: "pointer",
            }}
            onClick={openSetupScreen}
          >
            <PMSetupSteps
              title={getLocalizedMessage(
                language,
                "setupSteps.finishYourSetup"
              )}
              totalSteps={setupSteps.totalSteps}
              completedSteps={setupSteps.completedSteps}
            />
          </Box>
        )}
        <MenuSection
          entries={entries}
          hasSearchFunctionality={hasSearchFunctionality}
          searchFunction={searchFunction}
        />
        {buttonProps && (
          <Box
            data-tourid={buttonProps.tourid}
            sx={{
              p: 2,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PMButton {...buttonProps}></PMButton>
          </Box>
        )}
      </Drawer>
    </aside>
  );
};

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  backgroundColor: "#FAFAFA",
  "&:hover": {
    backgroundColor: "#FCFCFC",
  },
  borderRadius: "12px",
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#616161",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  paddingLeft: `calc(1em + ${theme.spacing(0.5)})`,
  "& .MuiInputBase-input": {
    padding: theme.spacing(2, 2, 2, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(2)})`,
    width: "100%",
  },
}));
